import { create } from 'zustand';
import type { components } from '@/api/generated/schema';

export type CurrentUser =
  components['schemas']['PixlideApplicationFeaturesAuthenticationCurrentUserResponse'];

interface AuthState {
  /**
   * Access token, conservé EN MÉMOIRE UNIQUEMENT.
   *
   * Décision de sécurité centrale du frontend : ni `localStorage`, ni `sessionStorage`.
   * Tout ce qui est stocké là est lisible par n'importe quel script exécuté sur la page,
   * y compris une dépendance npm compromise. Le refresh token, lui, vit dans un cookie
   * `HttpOnly` posé par l'API — donc invisible au JavaScript.
   *
   * Conséquence assumée : un rechargement de page perd l'access token. C'est sans
   * incidence, le client appelle alors `/auth/refresh`, dont le cookie est envoyé
   * automatiquement par le navigateur, et la session est restaurée en un aller-retour.
   */
  accessToken: string | null;
  user: CurrentUser | null;

  /** `false` jusqu'à la première tentative de restauration de session. */
  isBootstrapped: boolean;

  setSession: (accessToken: string) => void;
  setUser: (user: CurrentUser | null) => void;
  markBootstrapped: () => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  user: null,
  isBootstrapped: false,

  setSession: (accessToken) => set({ accessToken }),
  setUser: (user) => set({ user }),
  markBootstrapped: () => set({ isBootstrapped: true }),
  clear: () => set({ accessToken: null, user: null }),
}));

/**
 * Accès hors composant React, pour le client HTTP.
 *
 * Le client d'API n'est pas un composant : il ne peut pas utiliser de hook. Il lit donc
 * l'état directement via `getState()`, ce qui est le mode d'emploi prévu par Zustand.
 */
export const authStore = {
  getAccessToken: () => useAuthStore.getState().accessToken,
  setSession: (accessToken: string) => useAuthStore.getState().setSession(accessToken),
  clear: () => useAuthStore.getState().clear(),
};

/**
 * Propagation de la déconnexion aux autres onglets.
 *
 * Chaque onglet garde son propre access token en mémoire : sans ce canal, se déconnecter
 * dans un onglet laissait les autres naviguer jusqu'à l'expiration du jeton (15 minutes).
 * Seule la déconnexion VOLONTAIRE est diffusée — un échec de refresh au démarrage d'un
 * nouvel onglet ne doit pas fermer la session des onglets déjà ouverts.
 */
const logoutChannel =
  typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('pixlide-auth');

logoutChannel?.addEventListener('message', (event: MessageEvent) => {
  if (event.data === 'logout') {
    useAuthStore.getState().clear();
  }
});

export function broadcastLogout(): void {
  logoutChannel?.postMessage('logout');
}

/** Vrai si l'utilisateur détient la permission demandée. */
export function useHasPermission(permission: string): boolean {
  return useAuthStore((state) => state.user?.permissions.includes(permission) ?? false);
}
